import React, { useContext, useState } from 'react';
import { AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  LifeBuoy,
  Sparkles,
  Send,
  Search,
  ArrowRight,
  Compass,
  Award,
  Users2,
  Church,
  CreditCard,
  Settings,
  Video,
  Gauge,
  FileText,
  HelpCircle,
  CheckCircle2,
  Clock,
  AlertCircle,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Wrench,
  ShieldCheck,
  Activity,
  CreditCard as CardIcon,
  BarChart3,
  MessagesSquare
} from 'lucide-react';

// ============================================================
// SUPPORT — Wave 1 (visual only, no backend AI/ticket logic)
// Front door for the future AI Support & Knowledge System.
// ============================================================

const AI_CHIPS = [
  'Billing',
  'Affiliates',
  'Rewards',
  'Church Partner',
  'Creator Growth Engine',
  'Resource Toolkit',
  'Technical Support',
  'Team Management'
];

const SUGGESTED = [
  { id: 's1', title: 'Getting Started Guide',           desc: 'Set up your account, connect platforms, and publish your first post.', accent: 'cyan',  icon: Compass },
  { id: 's2', title: 'Affiliate Setup Guide',           desc: 'Activate referrals, share your link, and track conversions.',          accent: 'green', icon: Users2 },
  { id: 's3', title: 'Creator Growth Engine Overview',   desc: 'Understand your growth stages, tasks, and feedback loop.',             accent: 'cyan',  icon: Gauge },
  { id: 's4', title: 'Church Partner Certification',     desc: 'Complete training modules and qualify for partner payouts.',           accent: 'rose',  icon: Church },
  { id: 's5', title: 'Rewards Qualification Guide',      desc: 'Earn badges, tiers, bonuses, and plaques.',                            accent: 'amber', icon: Award },
  { id: 's6', title: 'Billing & Subscription Help',      desc: 'Manage plans, payment methods, invoices, and refunds.',                accent: 'green', icon: CreditCard }
];

const HELP_CATEGORIES = [
  { id: 'gs',   title: 'Getting Started',         icon: Compass,    accent: 'cyan',   count: 12 },
  { id: 'cc',   title: 'Content Creation',        icon: Video,      accent: 'pink',   count: 24 },
  { id: 'cge',  title: 'Creator Growth Engine',   icon: Gauge,      accent: 'cyan',   count: 18 },
  { id: 'aff',  title: 'Affiliates',              icon: Users2,     accent: 'green',  count: 14 },
  { id: 'rwd',  title: 'Rewards',                 icon: Award,      accent: 'amber',  count: 16 },
  { id: 'cp',   title: 'Church Partner',          icon: Church,     accent: 'rose',   count: 11 },
  { id: 'bil',  title: 'Billing',                 icon: CreditCard, accent: 'green',  count: 9 },
  { id: 'set',  title: 'Account Settings',        icon: Settings,   accent: 'violet', count: 13 }
];

const FAQS = [
  { id: 'q1', q: 'How do I qualify for rewards?',           a: 'Rewards are earned by completing milestones across publishing, engagement, and workflow completion. Open the Rewards page to see your current badge, tier progression, and qualification criteria.' },
  { id: 'q2', q: 'How do affiliate payouts work?',           a: 'Affiliate earnings are calculated from confirmed conversions and paid out monthly via your connected payout method. Pending payouts appear on your Affiliates dashboard.' },
  { id: 'q3', q: 'How does the Creator Growth Engine work?', a: 'The Creator Growth Engine unifies your flywheel, task engine, and decision engine into one operating system. It tells you where you are in your growth, what to focus on, and what to do next.' },
  { id: 'q4', q: 'How do I apply for Church Partner?',       a: 'Complete the certification training in Resource Toolkit → Church Partner Certification. Once all modules + the exam are passed, you can submit your application from the Church Partner page.' },
  { id: 'q5', q: 'How do I upgrade my plan?',                a: 'Go to Account → Billing and select a new plan. Upgrades take effect immediately and are pro-rated; downgrades apply at the next renewal.' },
  { id: 'q6', q: 'How do I invite team members?',            a: 'Open Account → Team to invite collaborators by email and assign roles (Admin, Editor, Viewer). Available on Agency+ plans.' }
];

const TICKETS = [
  { id: 'SUP-1042', subject: 'Billing Question',     category: 'Billing',     status: 'open',     updated: '2h ago',     priority: 'high' },
  { id: 'SUP-1038', subject: 'Affiliate Approval',   category: 'Affiliate',   status: 'pending',  updated: 'Yesterday',  priority: 'med'  },
  { id: 'SUP-1024', subject: 'Technical Issue',      category: 'Technical',   status: 'resolved', updated: '3 days ago', priority: 'low'  }
];

const STATUS_TONE = {
  open:     { label: 'Open',     border: 'border-amber-500/30', bg: 'bg-amber-500/10',  text: 'text-amber-300', icon: AlertCircle },
  pending:  { label: 'Pending',  border: 'border-cyan-500/30',  bg: 'bg-cyan-500/10',   text: 'text-cyan-300',  icon: Clock },
  resolved: { label: 'Resolved', border: 'border-green-500/30', bg: 'bg-green-500/10',  text: 'text-green-300', icon: CheckCircle2 }
};

const SYSTEM_STATUS = [
  { id: 'platform',  label: 'Platform Services', status: 'operational', icon: Activity },
  { id: 'payments',  label: 'Payments',          status: 'operational', icon: CardIcon },
  { id: 'analytics', label: 'Analytics',         status: 'operational', icon: BarChart3 },
  { id: 'community', label: 'Community',         status: 'operational', icon: MessagesSquare }
];

const RECENTLY_RESOLVED = [
  { id: 'rr1', title: 'Affiliate dashboard reporting delay',      resolvedOn: 'Feb 13, 2026' },
  { id: 'rr2', title: 'Reward status update visibility',           resolvedOn: 'Feb 10, 2026' },
  { id: 'rr3', title: 'Church Partner resource access issue',      resolvedOn: 'Feb 06, 2026' }
];

const CONTACT_CATEGORIES = [
  { value: 'general',   label: 'General' },
  { value: 'billing',   label: 'Billing' },
  { value: 'rewards',   label: 'Rewards' },
  { value: 'affiliate', label: 'Affiliate' },
  { value: 'church',    label: 'Church Partner' },
  { value: 'technical', label: 'Technical' },
  { value: 'team',      label: 'Team' },
  { value: 'other',     label: 'Other' }
];

// ---- helpers ----
const SourceChip = ({ source }) => (
  <span className="inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-[0.14em] text-[#555E6E]">
    <span className="w-1 h-1 rounded-full bg-[#3A4150]" />
    {source}
  </span>
);

const SectionHeader = ({ icon: Icon, accent = 'cyan', title, subtitle, source, right }) => (
  <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
    <div>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 text-${accent}-400`} />
        <h2 className="text-base font-semibold text-white">{title}</h2>
      </div>
      {subtitle && <p className="text-[11px] text-[#8A93A6] mt-0.5">{subtitle}</p>}
    </div>
    <div className="flex items-center gap-3">
      {source && <SourceChip source={source} />}
      {right}
    </div>
  </div>
);

// ============================================================
// MAIN
// ============================================================
export default function Support() {
  const { user } = useContext(AuthContext);
  const isAdmin = !!user?.is_internal_admin;

  const [aiQuery, setAiQuery] = useState('');
  const [helpQuery, setHelpQuery] = useState('');
  const [contactCategory, setContactCategory] = useState('general');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [feedback, setFeedback] = useState(null);

  const submitContact = (e) => {
    e.preventDefault();
    if (!contactSubject || !contactMessage) {
      toast.error('Please fill in all fields');
      return;
    }
    toast.success('Support request submitted — routed automatically');
    setContactSubject('');
    setContactMessage('');
  };

  const handleAskAI = () => {
    if (!aiQuery.trim()) {
      toast.info('Type a question to ask Consistency AI');
      return;
    }
    toast.info('AI assistant coming soon — Wave 2');
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl" data-testid="support-page">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <LifeBuoy className="h-6 w-6 text-cyan-400" />
            <h1 className="text-3xl font-bold text-white">Support</h1>
            <Badge variant="outline" className="text-[10px] border-cyan-500/30 text-cyan-300 bg-cyan-500/10 uppercase tracking-wider font-semibold">
              Support Hub
            </Badge>
            <Badge variant="outline" className="text-[10px] border-violet-500/30 text-violet-300 bg-violet-500/10 uppercase tracking-wider font-semibold">
              AI Ready
            </Badge>
            {isAdmin && (
              <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider font-semibold">
                Admin Preview
              </Badge>
            )}
          </div>
          <p className="text-[#8A93A6] max-w-2xl">
            Get help, find answers, and connect with the Consistency Support System.
          </p>
        </div>
      </div>

      {/* SECTION 1 — AI Support Assistant */}
      <section className="mb-7" data-testid="section-ai-assistant">
        <Card className="glass relative overflow-hidden border-cyan-500/20 ring-1 ring-cyan-500/10">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-violet-500/10 blur-3xl pointer-events-none" />
          <CardContent className="p-6 relative">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-cyan-300" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-white">Ask Consistency AI</h2>
                  <Badge variant="outline" className="text-[9px] border-violet-500/30 text-violet-300 bg-violet-500/10 uppercase tracking-wider font-semibold">
                    Coming Soon
                  </Badge>
                </div>
                <p className="text-[11px] text-[#8A93A6]">
                  Get instant answers, resource recommendations, troubleshooting help, and guidance before submitting a support request.
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <MessagesSquare className="h-4 w-4 text-cyan-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  placeholder="Describe your issue or question..."
                  className="pl-10 h-11 bg-[#0F1218]/80 border-white/10 text-white text-sm placeholder:text-[#555E6E]"
                  data-testid="ai-input"
                />
              </div>
              <Button
                onClick={handleAskAI}
                className="h-11 px-5 bg-cyan-500 hover:bg-cyan-400 text-[#0B0D12] font-semibold"
                data-testid="ask-ai-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                Ask AI
              </Button>
            </div>

            <div className="flex flex-wrap gap-1.5 mt-4">
              {AI_CHIPS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setAiQuery(c)}
                  className="text-[11px] uppercase tracking-wider rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 text-[#C9CFDA] px-3 py-1 transition-colors"
                  data-testid={`ai-chip-${c.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* SECTION 2 — Suggested Resources */}
      <section className="mb-7" data-testid="section-suggested">
        <SectionHeader
          icon={Sparkles}
          accent="violet"
          title="Suggested Resources"
          subtitle="Recommended resources based on your activity and recent questions."
          source="Knowledge Engine"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SUGGESTED.map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.id} className="glass border-white/5 hover:border-white/10 transition-colors" data-testid={`suggested-${s.id}`}>
                <CardContent className="p-3.5">
                  <div className="flex items-start gap-3 mb-2">
                    <div className={`w-8 h-8 rounded-md bg-${s.accent}-500/10 border border-${s.accent}-500/20 flex items-center justify-center shrink-0`}>
                      <Icon className={`h-3.5 w-3.5 text-${s.accent}-400`} />
                    </div>
                    <p className="text-[13px] font-semibold text-white leading-tight">{s.title}</p>
                  </div>
                  <p className="text-[11px] text-[#8A93A6] leading-relaxed mb-3">{s.desc}</p>
                  <button
                    type="button"
                    onClick={() => toast.info(`Opening "${s.title}"`)}
                    className={`text-[11px] font-semibold uppercase tracking-wider text-${s.accent}-300 hover:text-${s.accent}-200 inline-flex items-center gap-1.5`}
                  >
                    Open
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* SECTION 3 — Help Center */}
      <section className="mb-7" data-testid="section-help-center">
        <SectionHeader
          icon={HelpCircle}
          accent="cyan"
          title="Help Center"
          subtitle="Browse support articles and knowledge resources."
          source="Knowledge Base"
          right={
            <div className="relative w-full md:w-[260px]">
              <Search className="h-4 w-4 text-[#8A93A6] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={helpQuery}
                onChange={(e) => setHelpQuery(e.target.value)}
                placeholder="Search articles..."
                className="pl-9 h-9 bg-[#1C1F26] border-white/10 text-white text-xs"
                data-testid="help-search-input"
              />
            </div>
          }
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {HELP_CATEGORIES
            .filter((c) => !helpQuery || c.title.toLowerCase().includes(helpQuery.toLowerCase()))
            .map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toast.info(`Browsing "${c.title}" articles`)}
                  className="glass rounded-lg border border-white/5 hover:border-white/15 hover:translate-y-[-1px] bg-white/[0.015] hover:bg-white/[0.03] transition-all p-3 text-left"
                  data-testid={`help-category-${c.id}`}
                >
                  <div className={`w-8 h-8 rounded-md bg-${c.accent}-500/10 border border-${c.accent}-500/20 flex items-center justify-center mb-2`}>
                    <Icon className={`h-4 w-4 text-${c.accent}-400`} />
                  </div>
                  <p className="text-[13px] font-semibold text-white leading-tight">{c.title}</p>
                  <p className="text-[10px] text-[#8A93A6] mt-1">{c.count} articles</p>
                </button>
              );
            })}
        </div>
      </section>

      {/* SECTION 4 — Popular Questions */}
      <section className="mb-7" data-testid="section-faqs">
        <SectionHeader
          icon={HelpCircle}
          accent="amber"
          title="Frequently Asked Questions"
          subtitle="The most common answers, expanded inline."
          source="Knowledge Base"
        />
        <Card className="glass border-white/5">
          <CardContent className="p-2 sm:p-3">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f) => (
                <AccordionItem key={f.id} value={f.id} className="border-white/[0.06]" data-testid={`faq-${f.id}`}>
                  <AccordionTrigger className="text-left text-sm text-white hover:text-cyan-200 px-2 py-3 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[12px] text-[#C9CFDA] leading-relaxed px-2 pb-3">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      </section>

      {/* SECTION 5 — My Tickets */}
      <section className="mb-7" data-testid="section-tickets">
        <SectionHeader
          icon={FileText}
          accent="pink"
          title="My Tickets"
          subtitle="Track the status of your support requests."
          source="Ticket System"
          right={
            <Badge variant="outline" className="text-[10px] border-white/10 text-[#8A93A6] bg-white/[0.03] uppercase tracking-wider">
              {TICKETS.length} tickets
            </Badge>
          }
        />
        <Card className="glass border-white/5">
          <CardContent className="p-0">
            {/* Table header */}
            <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 border-b border-white/[0.05] text-[10px] uppercase tracking-[0.14em] text-[#555E6E] font-semibold">
              <div className="col-span-2">Ticket ID</div>
              <div className="col-span-4">Subject</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1">Updated</div>
              <div className="col-span-1 text-right">Action</div>
            </div>
            <div className="divide-y divide-white/[0.04]">
              {TICKETS.map((t) => {
                const s = STATUS_TONE[t.status];
                const Icon = s.icon;
                return (
                  <div
                    key={t.id}
                    className="grid grid-cols-2 md:grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-white/[0.02] transition-colors"
                    data-testid={`ticket-${t.id}`}
                  >
                    <div className="md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wider text-[#8A93A6] font-mono">#{t.id}</p>
                    </div>
                    <div className="md:col-span-4">
                      <p className="text-[13px] font-semibold text-white truncate">{t.subject}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-[11px] text-[#C9CFDA]">{t.category}</p>
                    </div>
                    <div className="md:col-span-2">
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${s.border} ${s.bg} ${s.text} gap-1`}>
                        <Icon className="h-2.5 w-2.5" />
                        {s.label}
                      </Badge>
                    </div>
                    <div className="md:col-span-1">
                      <p className="text-[10px] text-[#8A93A6]">{t.updated}</p>
                    </div>
                    <div className="md:col-span-1 flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[10px]"
                        onClick={() => toast.info(`Opening ticket #${t.id}`)}
                        data-testid={`view-ticket-${t.id}`}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* SECTION 6 + 7 — Contact Support + System Status */}
      <section className="mb-7 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="section-contact-and-status">
        {/* Contact Support */}
        <Card className="glass border-white/5 lg:col-span-2" data-testid="contact-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessagesSquare className="h-4 w-4 text-pink-400" />
                <p className="text-sm font-semibold text-white">Contact Support</p>
              </div>
              <SourceChip source="Routing engine" />
            </div>
            <form onSubmit={submitContact} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] mb-1.5 font-medium">Category</p>
                  <Select value={contactCategory} onValueChange={setContactCategory}>
                    <SelectTrigger className="h-9 bg-[#1C1F26] border-white/10 text-white text-xs" data-testid="contact-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT_CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] mb-1.5 font-medium">Subject</p>
                  <Input
                    value={contactSubject}
                    onChange={(e) => setContactSubject(e.target.value)}
                    placeholder="Brief summary"
                    className="h-9 bg-[#1C1F26] border-white/10 text-white text-xs"
                    data-testid="contact-subject-input"
                  />
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] mb-1.5 font-medium">Message</p>
                <Textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  rows={5}
                  placeholder="Describe what you need help with..."
                  className="bg-[#1C1F26] border-white/10 text-white text-xs"
                  data-testid="contact-message-input"
                />
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-[10px] text-[#8A93A6]">
                  Support requests are routed to the appropriate team automatically.
                </p>
                <Button
                  type="submit"
                  className="bg-pink-500 hover:bg-pink-400 text-white"
                  data-testid="submit-contact-btn"
                >
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                  Submit Request
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* System Status */}
        <Card className="glass border-white/5" data-testid="system-status-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-green-400" />
                <p className="text-sm font-semibold text-white">System Status</p>
              </div>
              <SourceChip source="Infrastructure" />
            </div>
            <div className="rounded-md border border-green-500/20 bg-green-500/[0.05] px-3 py-2 mb-3 flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400" />
              </span>
              <p className="text-xs text-green-300 font-semibold">All systems operational</p>
            </div>
            <div className="space-y-1.5">
              {SYSTEM_STATUS.map((s) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.id}
                    className="flex items-center gap-2.5 rounded-md border border-white/5 bg-white/[0.02] px-3 py-2"
                    data-testid={`status-${s.id}`}
                  >
                    <div className="w-6 h-6 rounded-md bg-green-500/10 border border-green-500/20 flex items-center justify-center shrink-0">
                      <Icon className="h-3 w-3 text-green-400" />
                    </div>
                    <p className="text-xs text-white flex-1 truncate">{s.label}</p>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-green-500/30 text-green-300 bg-green-500/10">
                      Operational
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* SECTION 8 — Recently Resolved */}
      <section className="mb-7" data-testid="section-recently-resolved">
        <SectionHeader
          icon={Wrench}
          accent="green"
          title="Recently Resolved Topics"
          subtitle="Common issues we've shipped fixes for recently."
          source="Engineering"
        />
        <Card className="glass border-white/5">
          <CardContent className="p-0">
            <div className="divide-y divide-white/[0.04]">
              {RECENTLY_RESOLVED.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.02] transition-colors"
                  data-testid={`resolved-${r.id}`}
                >
                  <div className="w-7 h-7 rounded-md bg-green-500/10 border border-green-500/20 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
                  </div>
                  <p className="text-[13px] font-medium text-white flex-1 truncate">{r.title}</p>
                  <span className="text-[10px] text-[#8A93A6] shrink-0 hidden sm:inline">{r.resolvedOn}</span>
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-green-500/30 text-green-300 bg-green-500/10 shrink-0">
                    Resolved
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* SECTION 9 — Feedback */}
      <section data-testid="section-feedback">
        <Card className="glass border-white/5">
          <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-white">Was this helpful?</p>
              <p className="text-[11px] text-[#8A93A6]">Your feedback helps us improve the support experience.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setFeedback('up'); toast.success('Thanks for the feedback!'); }}
                className={`gap-1.5 ${feedback === 'up' ? 'border-green-500/40 bg-green-500/10 text-green-200' : ''}`}
                data-testid="feedback-helpful-btn"
              >
                <ThumbsUp className="h-3.5 w-3.5" />
                Helpful
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setFeedback('down'); toast.info('We\'ll keep improving — try the AI assistant or submit a ticket.'); }}
                className={`gap-1.5 ${feedback === 'down' ? 'border-amber-500/40 bg-amber-500/10 text-amber-200' : ''}`}
                data-testid="feedback-need-more-btn"
              >
                <ThumbsDown className="h-3.5 w-3.5" />
                Need More Help
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
